import {
  Dimension,
  DimensionSet,
  ILogger,
  IMetrics,
  MetricName,
  MetricResult,
  Operation,
} from '@amzn/work-spaces-web-client-core-lib';
import {
  MetricsPublisherSink,
  VIEWER_METRIC,
  ViewerMetric,
} from '@amzn/dcv-web-sdk/workspaces';

/**
 * Anti-corruption adapter between the in-session viewer and this client's
 * metrics backend.
 *
 * The viewer emits metrics using its OWN vocabulary tokens (VIEWER_METRIC) and
 * never imports our metric enums. This adapter is the single place that maps
 * those tokens to core-lib's Operation/MetricResult/MetricName/Dimension enums
 * before delegating to the real Metrics instance. Keeping the mapping here means
 * a viewer-side vocabulary change or a core-lib enum rename only has to be
 * reconciled in one spot, and the viewer stays decoupled from the backend.
 */

// Keyed by the SDK's token literals, so tsc fails if the SDK adds an unmapped token.
type TokenOf<Group> = Group[keyof Group];

const OPERATION: Record<TokenOf<ViewerMetric['operation']>, Operation> = {
  [VIEWER_METRIC.operation.streaming]: Operation.Streaming,
  [VIEWER_METRIC.operation.session]: Operation.Session,
};

const RESULT: Record<TokenOf<ViewerMetric['result']>, MetricResult> = {
  [VIEWER_METRIC.result.success]: MetricResult.Success,
  [VIEWER_METRIC.result.error]: MetricResult.Error,
  [VIEWER_METRIC.result.fault]: MetricResult.Fault,
};

const METRIC_NAME: Record<TokenOf<ViewerMetric['name']>, MetricName> = {
  [VIEWER_METRIC.name.rtt]: MetricName.SessionRtt,
};

const DIMENSION: Record<TokenOf<ViewerMetric['dimension']>, Dimension> = {
  [VIEWER_METRIC.dimension.hostname]: Dimension.StreamingHostname,
  [VIEWER_METRIC.dimension.port]: Dimension.StreamingPort,
  [VIEWER_METRIC.dimension.transport]: Dimension.StreamingTransportType,
  [VIEWER_METRIC.dimension.ipVersion]: Dimension.StreamingIpVersion,
  [VIEWER_METRIC.dimension.gatewayType]: Dimension.StreamingGatewayType,
};

/**
 * Looks up the backend enum value a viewer token maps to, throwing if the token
 * has no mapping. `tokenKind` only labels the error (e.g. "operation").
 */
const requireMappedToken = <T>(
  mapping: Record<string, T>,
  token: string,
  tokenKind: string
): T => {
  const value = mapping[token];
  if (value === undefined) {
    throw new Error(
      `[viewerMetricsAdapter] unmapped viewer metric ${tokenKind} token: "${token}"`
    );
  }
  return value;
};

const mapDimensions = (
  dimensions: Record<string, unknown>,
  logger: Pick<ILogger, 'warn'>
): DimensionSet => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const mappedDimensions = {} as DimensionSet;
  for (const [token, value] of Object.entries(dimensions)) {
    if (value === null || value === undefined) {
      continue;
    }
    const dimension = (DIMENSION as Record<string, Dimension>)[token];
    if (dimension === undefined) {
      logger.warn(
        `[viewerMetricsAdapter] dropping unmapped viewer metric dimension token: "${token}"`
      );
      continue;
    }
    mappedDimensions[dimension] = String(value);
  }
  return mappedDimensions;
};

/**
 * Wraps a core-lib Metrics instance in the viewer's MetricsPublisherSink
 * contract. Inject the result as `dcv.observability.metricsPublisher`.
 */
export const makeViewerMetricsAdapter = (
  metrics: IMetrics,
  logger: Pick<ILogger, 'warn'>
): MetricsPublisherSink => ({
  emitWithDimensions: (operation, result, dimensions) =>
    metrics.emitWithDimensions(
      requireMappedToken(OPERATION, operation, 'operation'),
      requireMappedToken(RESULT, result, 'result'),
      mapDimensions(dimensions, logger)
    ),
  emitWithValue: (operation, name, value, dimensions) =>
    metrics.emitWithValue(
      requireMappedToken(OPERATION, operation, 'operation'),
      requireMappedToken(METRIC_NAME, name, 'name'),
      value,
      dimensions ? mapDimensions(dimensions, logger) : undefined
    ),
  emitWebSessionTimedMetricOperation: (summary, error) =>
    metrics.emitWebSessionTimedMetricOperation(summary as any, error),
});
